import React, {useState} from "react";
import Navbar from "./Navbar";

const FOCUS =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white";

const SECTIONS = [
    {
        id: "main-controller",
        label: "Main Controller",
        badge: "Robot Control",
        title: "Control the robot and monitor its position",
        intro: "Send commands, adjust individual joints, tune motion settings, and monitor the robot hand's position and orientation.",
        features: [
            {
                title: "Manual Command Console",
                image: "/command_panel.png",
                what: "The large editor accepts supported Python-style robot commands. For example, home() requests the configured home pose, while move_joint(1, 45) requests Joint 1 to move to 45 degrees.",
                why: "It lets you issue repeatable commands and short command sequences without using each on-screen control individually.",
                how: "Type the command in the editor and select Send Command. Use the exact supported command names and argument order from the command reference. Check that the robot's workspace is clear before sending movement commands."
            },
            {
                title: "Record and Function Controls",
                image: "/function_control.png",
                details: [
                    "Send Command: When you type the command in the editor and select Send Command. It will send all the command to the robot for execution.",
                    "Record: Enter a name in the record-name field, then select Record to save the command program under that name so it can be selected and reused later.",
                    "Saved-record dropdown: Select a previously saved program from the list before running or managing it.",
                    "Current Pos: Request or display the robot hand/end-effector's current position and orientation (X, Y, Z, RX, RY, RZ), when reported by the controller.",
                    "Current Joints: Display or request the current angle values of LINK_1 through LINK_6. Use these readings to check the robot's pose before teaching or repeating a movement.",
                    "Home: Send the robot to its configured home pose. Make sure the robot's path is clear before using it.",
                    "Delete: Remove the currently selected saved record. Check the selected name first; deleting a record may not be reversible.",
                    "Kill Switch: Emergency-stop control intended to stop robot motion immediately. After activating it, follow the controller's recovery/reset procedure before moving again."
                ]
            },
            {
                title: "Joint Controllers (LINK_1–LINK_6)",
                image: "/joint_control.png",
                what: "Each card represents one joint. The value field is the target angle in degrees (°), and the Min/Max labels show the configured angular range. You can type a degree value directly into the field or use − and + to adjust it.",
                why: "Direct entry makes it easier to request a specific joint angle, while the displayed limits help prevent requests outside the configured range.",
                how: "Enter an angle within that joint's range and commit it using the input's normal UI behavior. The − and + buttons adjust the value incrementally. Confirm the displayed and actual robot position before continuing.",
                details: [
                    "LINK_1: −170° to +170°.",
                    "LINK_2: −42° to +90°.",
                    "LINK_3: −89° to +52°.",
                    "LINK_4: −180° to +175°.",
                    "LINK_5: −105° to +105°.",
                    "LINK_6: −180° to +180°."
                ]
            },
            {
                title: "Speed, Acceleration and Deceleration",
                image: "/speed_control.png",
                details: [
                    "Speed: Sets the requested movement rate. A higher value requests faster movement; a lower value requests slower movement.",
                    "Acceleration (acc): Sets how quickly the robot ramps up toward the requested speed. Higher values request a faster ramp-up, while lower values make acceleration gentler.",
                    "Deceleration (dec): Sets how quickly the robot slows down. Higher values request a quicker slowdown; lower values make the slowdown more gradual.",
                    "Limits supplied for the controller: Speed 800–2000 steps/s; acceleration 300–500; deceleration 300–500. Confirm these values and units against the active controller's validation rules before relying on them, because the screenshot shows 20 in the fields."
                ]
            },
            {
                title: "Position and Orientation (X, Y, Z, RX, RY, RZ)",
                image: "/x,y,z_control.png",
                what: "X, Y and Z describe the end-effector's position in meters. RX, RY and RZ describe its orientation about the X, Y and Z axes in degrees. The dark value bars show the reported values; + and − adjust the values by the UI's configured increment.",
                why: "Cartesian position and orientation describe where the robot hand should be and how it should be rotated, rather than specifying each joint angle separately.",
                how: "Read X/Y/Z as distances and RX/RY/RZ as angles. For example, 0.10 m equals 10 cm. Use the incremental controls as configured by the UI, and remember that reachable positions depend on robot geometry, joint limits, and motion planning."
            }
        ]
    },
    {
        id: "template-studio",
        label: "Template Studio",
        badge: "Vision & Templates",
        title: "Save visual references and find objects",
        intro: "Choose a camera feed, register reference objects, preview saved templates, and search for matching objects.",
        features: [
            {
                title: "Active Camera Feed",
                image: "/template-camera-name.png",
                what: "The camera selector chooses the camera for object finding",
            },
            {
                title: "Get Reference Object",
                image: "/template-reference-capture.png",
                what: "This button starts the reference-object capture and registration action.",
                why: "The system needs a stored visual reference to know which appearance it should search for later.",
                how: "Select the intended camera feed, position the object clearly in view, and choose Get Reference Object. Follow any capture prompts shown by the application."
            },
            {
                title: "Saved Templates Registry",
                image: "/template-registry.png",
                what: "The registry lists saved template names and provides image preview, Find, and Delete actions. The Total counter shows how many templates are registered.",
                why: "It gives you one place to check which visual references are available and manage them.",
                how: "Review the template name before using its actions. Use the row's preview to verify the saved reference, Find to search for it, and Delete only when the template is no longer required."
            },
            {
                title: "Load Preview",
                image: "/template-preview.png",
                what: "Load Preview requests the stored image preview for a template.",
                why: "Checking the preview helps confirm that the saved reference is the intended object and is visually usable.",
                how: "Select Load Preview on the relevant row and inspect the image before running Find."
            },
            {
                title: "Find and Delete",
                image: "/template-find-delete.png",
                what: "Find runs a visual search using the selected template. Delete removes that template from the registry.",
                why: "Find checks whether the object can be located in the current view; Delete keeps the registry clear of unwanted or outdated references.",
                how: "Choose Find on the correct row and observe the application's result. Before selecting Delete, double-check the template name because removing a template may affect later searches."
            }
        ]
    },
    {
        id: "collision",
        label: "Collision & Safety",
        badge: "Workspace Safety",
        title: "Configure obstacle checks and virtual walls",
        intro: "Inspect the robot's working area, configure virtual wall boundaries, and review saved safety zones.",
        features: [
            {
                title: "Camera View and Workflow Navigation",
                image: "/collision-camera-navigation.png",
                what: "The camera selector chooses the camera for the object -finding process",
            },
            {
                title: "Scan Camera for Obstacles",
                image: "/collision-obstacle-scan.png",
                what: "This action starts the obstacle-scanning workflow for the robot's workspace.",
                why: "Identifying obstacles helps the system represent objects that may interfere with the robot's planned movement.",
                how: "Make sure the workspace and camera view are ready, then select Scan Camera for Obstacles. Review the resulting obstacle information before operating the robot."
            },
            {
                title: "Configure Wall Avoidance",
                image: "/collision-wall-avoidance.png",
                what: "This opens the setup used to configure virtual wall boundaries around the robot's workspace.",
                why: "Virtual boundaries define areas the robot should avoid when planning motion.",
                how: "Select Configure Wall Avoidance and enter the required boundary information in the setup interface. Check that the boundaries match the real workspace before enabling motion."
            },
            {
                title: "Saved Safety Zones",
                image: "/collision-safety-zones.png",
                what: "The table lists saved zones, including each box name, location (X, Y, Z), dimensions (width, depth, height), and available actions. Refresh List reloads the displayed list.",
                why: "Reviewing the saved locations and dimensions helps confirm that the configured safety barriers represent the intended workspace.",
                how: "Inspect each zone's name, coordinates, and dimensions. Use Refresh List after adding or changing zones. The screenshot shows an empty list when no zones have been saved."
            }
        ]
    },
    {
        id: "calibration",
        label: "Calibration",
        badge: "Camera & Joint Calibration",
        title: "Tune camera height and joint offsets",
        intro: "Choose the hardware camera, start calibration routines, set the end-effector height relative to the table, and adjust joint offsets.",
        features: [
            {
                title: "Active Hardware Feed",
                image: "/calibration-camera-feed.png",
                what: "The camera selector chooses the camera for calibration",
            },
            {
                title: "Camera Calibration Suite",
                image: "/calibration-suite.png",
                what: "Start Calibration begins the camera calibration workflow. End Effector Height from Table stores the configured height value relative to the table, and Update applies the entered value.",
                why: "The height reference helps the system relate the robot hand's position to the table surface during positioning tasks.",
                how: "Measure the end-effector height using the procedure for your robot, enter the value in the expected unit, and select Update. Start calibration when the camera and target are positioned as required."
            },
            {
                title: "Joint Selection",
                image: "/calibration-joint-selection.png",
                what: "The Joint 1–Joint 6 buttons select which joint you want to work with during calibration.",
                why: "Selecting a joint lets you focus on its calibration controls instead of changing every joint at once.",
                how: "Choose the joint you intend to calibrate, then review the corresponding offset and actuator controls before applying changes."
            },
            {
                title: "Joint Offset Matrix",
                image: "/calibration-offset-matrix.png",
                what: "The six offset fields hold correction values for Joint 1 through Joint 6. Update Offset applies the entered offsets; the screenshot shows 0.0 in each field.",
                why: "Offsets compensate for differences between the configured joint reference and the physical robot's alignment.",
                how: "Determine the correction using the robot's calibration procedure, enter it in the matching joint field, and select Update Offset. Change one value at a time when troubleshooting and verify the resulting pose."
            },
            {
                title: "Joint Actuator Controllers",
                image: "/joint_control.png",
                what: "Each LINK card shows a joint value, its displayed minimum and maximum angles, and minus/plus controls for adjustment.",
                why: "These controls let you inspect and make small joint adjustments while checking calibration results."
            }
        ]
    }
];

const TABS = [
    {
        id: "moving",
        label: "How the arm moves",
        title: "From a spot in space to turning joints",
        intro: "You don't have to figure out how much each joint should turn. The computer does that math for you.",
        points: [
            "You say where the hand should go: left or right, forward or back, up or down, and how it should tilt.",
            "The computer works out the right angle for each of the 6 joints. This math is called inverse kinematics.",
            "Before anything moves, it checks the plan against the robot's size and limits, so no joint turns too far.",
        ],
    },
    {
        id: "talking",
        label: "How it talks to the computer",
        title: "From a button click to a moving motor",
        intro: "Every click on this page turns into a small message that travels to the robot.",
        points: [
            "This page sends the message over a WebSocket, which is a live connection that stays open.",
            "The robot's computer reads the message and passes it to your Arduino firmware through a serial connection.",
            "The motors move right away, and the robot reports back so this page can show where the arm is.",
        ],
    },
];

const WORDS = [
    ["Joint", "A place where the arm can turn, like your elbow or wrist. Endhiran has 6."],
    ["Degrees", "The unit for turning. A full circle is 360 degrees."],
    ["Meters", "The unit for distance. One meter is a little more than three feet."],
    ["x, y, z", "Three numbers that name a spot in space: how far across, how far forward, and how high."],
    ["Gripper", "The robot's fingers. It opens to let go and closes to hold something."],
    ["Camera on the hand", "A camera fixed to the robot's hand, so it sees what the hand sees."],
];

const CATEGORIES = ["All", "Joints", "Hand position", "Start position", "Gripper", "Camera"];

const COMMANDS = [
    {
        name: "move_joint(joint, degrees)",
        category: "Joints",
        example: "move_joint(1, 45.0)",
        desc: "Turns one joint to an angle. Joints are numbered 1 to 6. The robot checks that the move is safe before it starts.",
    },
    {
        name: "move_joints(j1, j2, j3, j4, j5, j6)",
        category: "Joints",
        example: "move_joints(0.0, -30.0, 90.0, 0.0, 45.0, 0.0)",
        desc: "Turns all 6 joints at the same time. Give one angle for each joint. If you skip one, it stays where it is.",
    },
    {
        name: "move_arm(x, y, z, rx, ry, rz)",
        category: "Hand position",
        example: "move_arm(0.25, 0.0, 0.15, 0.0, 180.0, 0.0)",
        desc: "Moves the robot's hand to a spot in space. x, y and z are the spot, in meters. rx, ry and rz tilt the hand, in degrees. The robot plans a path that avoids bumping into things.",
    },
    {
        name: "place(x, y, z, rx, ry, rz)",
        category: "Hand position",
        example: "place(0.30, 0.10, 0.05, 0.0, 180.0, 0.0)",
        desc: "Moves the hand to a spot and tilts it, using the same numbers as move_arm. Use it to set an object down.",
    },
    {
        name: "home()",
        category: "Start position",
        example: "home()",
        desc: "Sends every joint back to 0 degrees. This is the robot's starting pose.",
    },
    {
        name: "open_gripper()",
        category: "Gripper",
        example: "open_gripper()",
        desc: "Opens the gripper so the robot lets go of an object.",
    },
    {
        name: "close_gripper()",
        category: "Gripper",
        example: "close_gripper()",
        desc: "Closes the gripper so the robot can hold an object tightly.",
    },
    {
        name: "find(template_name, z)",
        category: "Camera",
        example: 'find("object_marker",0.1)',
        desc: "Uses the camera to look for an object by name. When it spots the object, the robot works out where it is and moves its hand above it.",
    },
];

const Help = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [category, setCategory] = useState("All");
    const [activeTab, setActiveTab] = useState("moving");
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, idx) => {
        navigator.clipboard?.writeText(text).catch(() => {
        });
        setCopiedIndex(idx);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const query = searchQuery.trim().toLowerCase();
    const filteredCommands = COMMANDS.filter(
        (cmd) =>
            (category === "All" || cmd.category === category) &&
            (cmd.name.toLowerCase().includes(query) ||
                cmd.desc.toLowerCase().includes(query)),
    );

    const tab = TABS.find((t) => t.id === activeTab);

    return (
        <div className="min-h-screen bg-white text-slate-800 pb-16">
            <Navbar/>

            <div
                className="w-full max-w-[1800px] mx-auto px-4 sm:px-6 xl:px-10 2xl:px-14 py-8 lg:py-10 space-y-14 lg:space-y-16">

                <section className="space-y-6">
                    <div className="max-w-3xl space-y-4">
                        <span
                            className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                            Robotics Control Center · Help
                        </span>
                        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-slate-900">
                            Control Center Guide
                        </h1>
                        <p className="text-base sm:text-lg leading-relaxed text-slate-600">
                            A visual guide to the main controller, template management, workspace safety, and
                            calibration tools. Each section explains the controls shown in the screenshots and what they
                            are used for.
                        </p>
                    </div>
                </section>
                <section
                    className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xl shadow-slate-200/50">
                    <div className="max-w-3xl space-y-3">
                        <span
                            className="rounded-full border border-sky-500/30 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                            Practical Guide
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                            Use Case: How to Pick Up an Object
                        </h2>
                        <p className="text-sm sm:text-base leading-relaxed text-slate-600">
                            Follow this step-by-step workflow to approach an object, grip it, and perform a
                            pick-and-place operation using the Command Panel, Joint Controls, Current Joint readings,
                            and Saved Records.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
                        {/* Step 1 */}
                        <div className="flex flex-col rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-4">
                            <div className="flex items-center gap-3">
                                <span
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-sm font-bold text-emerald-800">
                                    01
                                </span>
                                <h3 className="font-semibold text-slate-900 text-base">Open Gripper & Move Near
                                    Object</h3>
                            </div>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                First, make sure the robot gripper is wide open to receive the object. Use the command
                                panel to send <code
                                className="text-sky-700 font-mono bg-sky-50 px-1 py-0.5 rounded">open_gripper()</code>[cite:
                                1].
                                Then, use the <strong>Joint Controls (LINK_1–LINK_6)</strong> to move individual joints
                                incrementally until the arm's end-effector is positioned close to your target
                                object.
                            </p>
                            <div className="rounded-lg border border-slate-200 bg-white p-2 overflow-hidden">
                                <img src="/joint_control.png" alt="Joint Controls"
                                     className="h-auto max-h-[220px] w-full rounded object-contain"/>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex flex-col rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-4">
                            <div className="flex items-center gap-3">
                                <span
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-sm font-bold text-emerald-800">
                                    02
                                </span>
                                <h3 className="font-semibold text-slate-900 text-base">Check, Append, and Save Using
                                    Current Joints</h3>
                            </div>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                Once the arm is properly aligned next to the object, click the <strong>Current
                                Joints</strong> control to instantly append the current angle values of LINK_1 through
                                LINK_6 as a movement command into your command editor. Enter a program name in
                                the record field and select <strong>Record</strong> so you can easily reuse this pick
                                pose later from the saved-record dropdown.
                            </p>
                            <div className="rounded-lg border border-slate-200 bg-white p-2 overflow-hidden">
                                <img src="/function_control.png" alt="Function Controls"
                                     className="h-auto max-h-[220px] w-full rounded object-contain"/>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="flex flex-col rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-4">
                            <div className="flex items-center gap-3">
                                <span
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-sm font-bold text-emerald-800">
                                    03
                                </span>
                                <h3 className="font-semibold text-slate-900 text-base">Grip the Object Securely</h3>
                            </div>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                With the gripper precisely positioned around or over the object, send the closure
                                instruction from the <strong>Manual Command Console</strong> by typing <code
                                className="text-sky-700 font-mono bg-sky-50 px-1 py-0.5 rounded">close_gripper()</code> and
                                clicking <strong>Send
                                Command</strong>. Verify that the object is held firmly before executing any
                                lifting routines.
                            </p>
                            <div className="rounded-lg border border-slate-200 bg-white p-2 overflow-hidden">
                                <img src="/command_panel.png" alt="Command Panel"
                                     className="h-auto max-h-[220px] w-full rounded object-contain"/>
                            </div>
                        </div>

                        {/* Step 4 */}
                        <div className="flex flex-col rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-4">
                            <div className="flex items-center gap-3">
                                <span
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-sm font-bold text-emerald-800">
                                    04
                                </span>
                                <h3 className="font-semibold text-slate-900 text-base">Lift, Place, and Reuse Saved
                                    Records</h3>
                            </div>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                Finally, use the Cartesian position sliders (<code
                                className="text-sky-700 font-mono bg-sky-50 px-1 py-0.5 rounded">X,
                                Y, Z</code>) or issue a <code
                                className="text-sky-700 font-mono bg-sky-50 px-1 py-0.5 rounded">place(x, y, z, rx, ry,
                                rz)</code> or <code
                                className="text-sky-700 font-mono bg-sky-50 px-1 py-0.5 rounded"> move_joints()</code> command
                                to carry the object to its destination
                                drop-zone. Re-open
                                the gripper via <code
                                className="text-sky-700 font-mono bg-sky-50 px-1 py-0.5 rounded">open_gripper()</code> to
                                release the item. You can load your saved pickup-and-place routine anytime from
                                the <strong>saved-record dropdown</strong> to repeat the entire action
                                automatically.
                            </p>
                            <div className="rounded-lg border border-slate-200 bg-white p-2 overflow-hidden">
                                <img src="/x,y,z_control.png" alt="Position Controls"
                                     className="h-auto max-h-[220px] w-full rounded object-contain"/>
                            </div>
                        </div>
                    </div>
                </section>

                {/* APP SECTIONS BREAKDOWN */}
                <section className="space-y-8">
                    <div
                        className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Explore the
                                interface</p>
                        </div>
                        <p className="max-w-xl text-sm leading-relaxed text-slate-600">Use the screenshots as a
                            reference, then read the descriptions to understand each panel and its controls.</p>
                    </div>

                    <div className="space-y-8">
                        {SECTIONS.map((sec, idx) => (
                            <article key={sec.id}
                                     className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50">
                                <div className="space-y-3 p-5 sm:p-7">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span
                                            className="rounded-full border border-emerald-500/30 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{sec.badge}</span>
                                        <span className="text-xs font-medium text-slate-400">0{idx + 1} / 04</span>
                                    </div>
                                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900">{sec.label}</h3>
                                    <h4 className="text-sm font-semibold text-sky-700">{sec.title}</h4>
                                    <p className="max-w-3xl text-sm sm:text-base leading-relaxed text-slate-600">{sec.intro}</p>
                                </div>

                                <div className="border-y border-slate-200 bg-slate-50 p-4 sm:p-6 lg:p-8">
                                    <div className="mb-5">
                                        <h5 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Interface
                                            guide</h5>
                                        <p className="mt-2 text-sm text-slate-500">Each control is shown with its own
                                            image and a description underneath.</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
                                        {sec.features.map((feature, featureIdx) => (
                                            <article key={feature.title}
                                                     className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                                                <div
                                                    className="flex items-start gap-3 border-b border-slate-200 px-4 py-4 sm:px-5">
                                                    <span
                                                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-sm font-extrabold text-emerald-700">
                                                        {String(featureIdx + 1).padStart(2, "0")}
                                                    </span>
                                                    <div className="min-w-0">
                                                        <h5 className="font-semibold text-slate-900">{feature.title}</h5>
                                                    </div>
                                                </div>
                                                <div
                                                    className="flex min-h-0 items-center justify-center border-b border-slate-200 bg-slate-50 p-2 sm:p-3">
                                                    <img src={feature.image}
                                                         alt={feature.title}
                                                         loading="lazy"
                                                         className="block h-auto max-h-[360px] w-full rounded-lg object-contain"/>
                                                </div>
                                                <div className="flex-1 space-y-4 p-4 sm:p-5">
                                                    <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-700 marker:text-emerald-600">
                                                        {feature.what && <li>{feature.what}</li>}
                                                        {feature.why && <li>{feature.why}</li>}
                                                        {feature.how && <li>{feature.how}</li>}
                                                    </ul>
                                                    {feature.details?.length > 0 && (
                                                        <div>
                                                            <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-700 marker:text-emerald-600">
                                                                {feature.details.map((point, pointIdx) => (
                                                                    <li key={`${feature.title}-detail-${pointIdx}`}>
                                                                        {point}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* STEP-BY-STEP USE CASE: PICK AND PLACE */}

                {/* HOW IT WORKS */}
                <section className="space-y-5">
                    <h2 className="text-2xl font-bold text-slate-900">How it works</h2>
                    <div
                        role="tablist"
                        aria-label="How Endhiran works"
                        className="flex gap-2 overflow-x-auto"
                    >
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                role="tab"
                                id={`tab-${t.id}`}
                                aria-selected={activeTab === t.id}
                                aria-controls="tab-panel"
                                onClick={() => setActiveTab(t.id)}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold shrink-0 border transition-colors ${FOCUS} ${
                                    activeTab === t.id
                                        ? "bg-slate-900 text-amber-300 border-slate-900 shadow"
                                        : "text-slate-600 border-slate-200 bg-white hover:text-slate-900 hover:border-slate-300"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div
                        id="tab-panel"
                        role="tabpanel"
                        aria-labelledby={`tab-${tab.id}`}
                        className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 shadow-sm"
                    >
                        <h3 className="text-xl font-bold text-slate-900">{tab.title}</h3>
                        <p className="leading-relaxed max-w-prose text-slate-600">{tab.intro}</p>
                        <ul className="space-y-3 max-w-prose">
                            {tab.points.map((p) => (
                                <li key={p} className="flex gap-3 leading-relaxed text-slate-700">
                                    <span
                                        className="mt-2.5 h-2 w-2 rounded-full bg-amber-500 shrink-0"
                                        aria-hidden="true"
                                    />
                                    <span>{p}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* WORDS */}
                <section className="space-y-5">
                    <h2 className="text-2xl font-bold text-slate-900">
                        Words you'll see
                    </h2>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
                        {WORDS.map(([term, meaning]) => (
                            <div key={term} className="border-l-2 border-amber-500 pl-4">
                                <dt className="font-bold text-slate-900">{term}</dt>
                                <dd className="leading-relaxed mt-1 text-slate-600">{meaning}</dd>
                            </div>
                        ))}
                    </dl>
                </section>

                {/* COMMANDS */}
                <section className="space-y-5">
                    <div className="space-y-2">
                        <h2 className="text-2xl font-bold text-slate-900">
                            Commands you can use
                        </h2>
                        <p className="leading-relaxed max-w-prose text-slate-600">
                            Each command is one instruction for the robot. Search by word,
                            pick a group, then press Copy to grab an example you can paste
                            into your program.
                        </p>
                    </div>

                    <div className="space-y-3">
                        <label htmlFor="command-search" className="sr-only">
                            Search commands
                        </label>
                        <input
                            id="command-search"
                            type="text"
                            placeholder="Search, for example: gripper or move"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={`w-full sm:w-96 bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 placeholder-slate-400 shadow-sm ${FOCUS}`}
                        />
                        <div className="flex flex-wrap gap-2">
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c}
                                    aria-pressed={category === c}
                                    onClick={() => setCategory(c)}
                                    className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${FOCUS} ${
                                        category === c
                                            ? "bg-amber-500 text-slate-950 border-amber-500 font-semibold"
                                            : "text-slate-700 border-slate-300 bg-white hover:border-slate-400"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>

                    {filteredCommands.length > 0 ? (
                        <ul className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-200 shadow-sm">
                            {filteredCommands.map((cmd) => {
                                const idx = COMMANDS.indexOf(cmd);
                                return (
                                    <li key={cmd.name} className="p-5 space-y-3">
                                        <div>
                                            <h3 className="font-mono font-bold text-amber-700 break-words">
                                                {cmd.name}
                                            </h3>
                                            <p className="text-sm text-slate-500 mt-0.5">
                                                {cmd.category}
                                            </p>
                                        </div>
                                        <p className="leading-relaxed max-w-prose text-slate-700">{cmd.desc}</p>
                                        <div
                                            className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg pl-4 pr-2 py-2">
                                            <code
                                                className="font-mono text-sm text-sky-700 overflow-x-auto whitespace-nowrap">
                                                {cmd.example}
                                            </code>
                                            <button
                                                onClick={() => handleCopy(cmd.example, idx)}
                                                className={`px-3.5 py-1.5 rounded-md bg-white border border-slate-300 hover:bg-slate-100 text-sm font-semibold text-slate-800 shadow-sm shrink-0 ${FOCUS}`}
                                            >
                                                {copiedIndex === idx ? "Copied" : "Copy"}
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <div
                            className="bg-white border border-slate-200 rounded-xl p-8 text-center space-y-3 shadow-sm">
                            <p className="text-slate-600">No commands match your search. Try a shorter word.</p>
                            <button
                                onClick={() => {
                                    setSearchQuery("");
                                    setCategory("All");
                                }}
                                className={`px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm shadow-sm ${FOCUS}`}
                            >
                                Show all commands
                            </button>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Help;